import type { App } from "@open-booking/server";
import type {
	IntegrationProvider,
	UpdatePlunkIntegrationPayload,
	UpdateStripeIntegrationPayload,
	UpdateSwiklyIntegrationPayload,
} from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type GetIntegrationsResponse = App["~Routes"]["integrations"]["get"]["response"]["200"];
type UpsertIntegrationResponse = App["~Routes"]["integrations"]["stripe"]["put"]["response"]["200"];
type TestConnectionResponse = App["~Routes"]["integrations"][":provider"]["test"]["post"]["response"]["200"];

export const integrationApi = createApi({
	reducerPath: "integrationApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["Integration"],
	endpoints: (builder) => ({
		getIntegrations: builder.query<GetIntegrationsResponse, void>({
			query: (): EdenCall<GetIntegrationsResponse> => (client) => client.integrations.get(),
			providesTags: ["Integration"],
		}),
		updateStripeIntegration: builder.mutation<UpsertIntegrationResponse, UpdateStripeIntegrationPayload>({
			query:
				(payload): EdenCall<UpsertIntegrationResponse> =>
				(client) =>
					client.integrations.stripe.put(payload),
			invalidatesTags: ["Integration"],
		}),
		updateSwiklyIntegration: builder.mutation<UpsertIntegrationResponse, UpdateSwiklyIntegrationPayload>({
			query:
				(payload): EdenCall<UpsertIntegrationResponse> =>
				(client) =>
					client.integrations.swikly.put(payload),
			invalidatesTags: ["Integration"],
		}),
		updatePlunkIntegration: builder.mutation<UpsertIntegrationResponse, UpdatePlunkIntegrationPayload>({
			query:
				(payload): EdenCall<UpsertIntegrationResponse> =>
				(client) =>
					client.integrations.plunk.put(payload),
			invalidatesTags: ["Integration"],
		}),
		testIntegration: builder.mutation<TestConnectionResponse, IntegrationProvider>({
			query:
				(provider): EdenCall<TestConnectionResponse> =>
				(client) =>
					client.integrations({ provider }).test.post(),
			invalidatesTags: ["Integration"],
		}),
		deleteIntegration: builder.mutation<{ message: string }, IntegrationProvider>({
			query:
				(provider): EdenCall<{ message: string }> =>
				(client) =>
					client.integrations({ provider }).delete(),
			invalidatesTags: ["Integration"],
		}),
	}),
});

export const {
	useGetIntegrationsQuery,
	useUpdateStripeIntegrationMutation,
	useUpdateSwiklyIntegrationMutation,
	useUpdatePlunkIntegrationMutation,
	useTestIntegrationMutation,
	useDeleteIntegrationMutation,
} = integrationApi;
